import { DASHBOARD_QUERY_KEYS } from '@/entities/dashboard'
import type { PaginatedResponse } from '@/shared/api/types/api-response'
import type {
  CreateTenantPayload,
  ILanguage,
  ITenantLanguage,
  ITenantThemeConfig,
  SettingKeyCreateRequest,
  TenantLanguageListParams,
  TenantLanguageUpdateRequest,
  TenantListParams,
  UpdateTenantInfoPayload,
  UpdateTenantPayload,
} from '@/shared/api/types/tenant-types'
import { useMemo } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { TENANT_DESTINATION_QUERY_KEYS } from '@/entities/tenant-destination/api/tenant-destination-queries'
import { TOUR_QUERY_KEYS } from '@/entities/tour/api/tour-queries'
import {
  addTenantLanguage,
  createSettingKey,
  createTenant,
  deleteTenant,
  deleteTenantLanguage,
  getAllTenantLanguages,
  getDefaultTourSetting,
  getLanguages,
  getTenantById,
  getTenantLanguagesPage,
  getTenants,
  getTenantSettingByKey,
  listTenantSettings,
  reorderTenantLanguages,
  toggleTenantStatus,
  updateDefaultTourSetting,
  updateTenant,
  updateTenantDefaultLanguage,
  updateTenantInfo,
  updateTenantSettingValue,
  updateTenantTheme,
} from '@/shared/api/services/tenant-service'
import { getNextPaginatedPageParam } from '@/shared/api/utils/pagination'

const TENANT_QUERY_KEYS = {
  all: ['tenants'] as const,
  listPrefix: () => [...TENANT_QUERY_KEYS.all, 'list'] as const,
  list: (params: TenantListParams) => [...TENANT_QUERY_KEYS.all, 'list', params] as const,
  detailPrefix: (id: string) => [...TENANT_QUERY_KEYS.all, 'detail', id] as const,
  detail: (id: string, withSettings?: boolean) =>
    [...TENANT_QUERY_KEYS.all, 'detail', id, withSettings ? 'full' : 'lite'] as const,
  defaultLanguage: (tenantId?: string) =>
    [...TENANT_QUERY_KEYS.all, 'default-language', tenantId ?? 'self'] as const,
  languages: (tenantId?: string, params?: TenantLanguageListParams) =>
    [...TENANT_QUERY_KEYS.all, 'languages', tenantId ?? 'self', params ?? null] as const,
  allLanguages: ['tenants', 'all-languages'] as const,
  defaultTour: (tenantId?: string) =>
    [...TENANT_QUERY_KEYS.all, 'default-tour', tenantId ?? 'self'] as const,
}

const SETTING_QUERY_KEYS = {
  all: ['tenant-settings'] as const,
  list: (tenantId: string, params?: { current?: number; pageSize?: number; keywords?: string }) =>
    [...SETTING_QUERY_KEYS.all, 'list', tenantId, params] as const,
  item: (id: string) => [...SETTING_QUERY_KEYS.all, 'item', id] as const,
}

export function useTenantsQuery(params: TenantListParams, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: TENANT_QUERY_KEYS.list(params),
    queryFn: async () => getTenants(params),
    enabled: options.enabled ?? true,
  })
}

export function useTenantByIdQuery(id: string, options?: { withSettings?: boolean }) {
  const withSettings = options?.withSettings ?? false
  return useQuery({
    queryKey: TENANT_QUERY_KEYS.detail(id, withSettings),
    queryFn: async () => getTenantById(id, { withSettings }),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useTenantLanguages(tenantId: string, options?: { pageSize?: number }) {
  const pageSize = options?.pageSize ?? 20
  const query = useInfiniteQuery<PaginatedResponse<ITenantLanguage>, Error>({
    queryKey: [...TENANT_QUERY_KEYS.languages(tenantId), 'infinite', { pageSize }] as const,
    queryFn: async ({ pageParam }) =>
      getTenantLanguagesPage(tenantId, {
        current: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatedPageParam,
    enabled: !!tenantId,
    staleTime: 0,
    refetchOnMount: 'always',
  })
  const items = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data])
  return { ...query, items }
}

/** Loads all of a tenant's languages in one query (no infinite paging). */
export function useAllTenantLanguagesQuery(tenantId: string) {
  return useQuery({
    queryKey: [...TENANT_QUERY_KEYS.languages(tenantId), 'all'] as const,
    queryFn: async (): Promise<ITenantLanguage[]> => getAllTenantLanguages(tenantId),
    enabled: !!tenantId,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useLanguagesQuery(fetchingLanguages: boolean) {
  return useQuery({
    queryKey: TENANT_QUERY_KEYS.allLanguages,
    queryFn: async (): Promise<{ data: ILanguage[] }> => getLanguages(),
    enabled: fetchingLanguages,
  })
}

export function useCreateTenantMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateTenantPayload) => createTenant(payload),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.all }),
  })
}

export function useUpdateTenantMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateTenantPayload }) =>
      updateTenant(id, payload),
    onSuccess: async (_data, { id }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.listPrefix() }),
        queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.detailPrefix(id) }),
        queryClient.invalidateQueries({ queryKey: SETTING_QUERY_KEYS.all }),
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.all }),
      ])
    },
  })
}

export function useUpdateTenantInfoMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateTenantInfoPayload }) =>
      updateTenantInfo(id, payload.localizedData),
    onSuccess: async (_data, { id }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.listPrefix() }),
        queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.detailPrefix(id) }),
      ])
    },
  })
}

export function useDeleteTenantMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => deleteTenant(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.all })
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.all })
    },
  })
}

export function useToggleTenantStatusMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => toggleTenantStatus(id),
    onSuccess: async (_data, id) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.listPrefix() }),
        queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.detailPrefix(id) }),
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.all }),
      ])
    },
  })
}

export function useUpdateTenantThemeMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, themeConfig }: { id: string; themeConfig: ITenantThemeConfig }) =>
      updateTenantTheme(id, themeConfig),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.detailPrefix(id) })
    },
  })
}

export function useAddTenantLanguageMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, languageCode }: { id: string; languageCode: string }) =>
      addTenantLanguage(id, languageCode),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.languages(id) })
      void queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.detailPrefix(id) })
    },
  })
}

export function useReorderTenantLanguagesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: TenantLanguageUpdateRequest[] }) =>
      reorderTenantLanguages(id, updates),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.languages(id) })
    },
  })
}

export function useDeleteTenantLanguageMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, tenantLanguageId }: { id: string; tenantLanguageId: string }) =>
      deleteTenantLanguage(tenantLanguageId, id),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.languages(id) })
    },
  })
}

export function useUpdateTenantDefaultLanguageMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, languageCode }: { id: string; languageCode: string }) =>
      updateTenantDefaultLanguage(id, languageCode),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.detailPrefix(id) })
      void queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.languages(id) })
      void queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.defaultLanguage(id) })
      void queryClient.invalidateQueries({ queryKey: SETTING_QUERY_KEYS.item(id) })
    },
  })
}

export function useUpdateDefaultTourMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateDefaultTourSetting,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.all }),
        queryClient.invalidateQueries({ queryKey: TOUR_QUERY_KEYS.all }),
        queryClient.invalidateQueries({ queryKey: TENANT_DESTINATION_QUERY_KEYS.all }),
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.all }),
      ])
    },
  })
}

export function useDefaultTourSettingQuery(tenantId?: string) {
  return useQuery({
    queryKey: TENANT_QUERY_KEYS.defaultTour(tenantId),
    queryFn: async () => getDefaultTourSetting(tenantId),
  })
}

export function useTenantSettingsQuery(
  tenantId: string,
  params?: { current?: number; pageSize?: number; keywords?: string },
) {
  return useQuery({
    queryKey: SETTING_QUERY_KEYS.list(tenantId, params),
    queryFn: async () => listTenantSettings(tenantId, params),
    enabled: !!tenantId,
  })
}

export function useTenantSettingByKeyQuery(tenantId: string, key: string) {
  return useQuery({
    queryKey: [...SETTING_QUERY_KEYS.all, 'by-key', tenantId, key],
    queryFn: async () => getTenantSettingByKey(tenantId, key),
    enabled: !!tenantId && !!key,
  })
}

export function useUpdateTenantSettingMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      tenantId,
      key,
      value,
    }: {
      tenantId: string
      key: string
      value: string
    }) => updateTenantSettingValue(tenantId, key, value),
    // Await the refetch so mutateAsync resolves only once fresh values are in cache;
    // otherwise the form drops its pending edit and shows the stale cached value.
    onSuccess: async (_data, { tenantId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: SETTING_QUERY_KEYS.all }),
        queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.detailPrefix(tenantId) }),
      ])
    },
  })
}

export function useCreateSettingKeyMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: SettingKeyCreateRequest) => createSettingKey(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SETTING_QUERY_KEYS.all })
    },
  })
}
